/**
 * UndoStore<T> is an immutable undo/redo history over values of type T.
 *
 * It holds three things:
 *   - past:    states older than the present, oldest first
 *   - present: the current state
 *   - future:  states newer than the present, next-to-redo first
 *             (populated by undoing)
 *
 * Every operation returns a *new* UndoStore rather than mutating in place, so
 * the store composes naturally with React reducers and preserves structural
 * sharing of the underlying immutable trees of type T.
 *
 * The store deliberately knows nothing about *when* a state is worth recording.
 * Callers decide that: transient edits (dragging a node, typing into a field)
 * should update their own live state and only call `record` once, when the
 * gesture completes — so a whole drag becomes a single undoable step rather
 * than one step per animation frame.
 */
export class UndoStore<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];

  constructor(present: T, past: readonly T[] = [], future: readonly T[] = []) {
    this.present = present;
    this.past = past;
    this.future = future;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /**
   * Record a new present state as a distinct undoable step, pushing the old
   * present onto the past and clearing the redo stack. Recording the value
   * that already equals the present is a no-op.
   */
  record(next: T): UndoStore<T> {
    if (Object.is(next, this.present)) return this;
    return new UndoStore<T>(next, [...this.past, this.present], []);
  }

  /** Move one step back in history. No-op if there is nothing to undo. */
  undo(): UndoStore<T> {
    if (!this.canUndo) return this;
    const previous = this.past[this.past.length - 1];
    const newPast = this.past.slice(0, -1);
    return new UndoStore<T>(previous, newPast, [this.present, ...this.future]);
  }

  /** Move one step forward in history. No-op if there is nothing to redo. */
  redo(): UndoStore<T> {
    if (!this.canRedo) return this;
    const [next, ...newFuture] = this.future;
    return new UndoStore<T>(next, [...this.past, this.present], newFuture);
  }
}
